import React from "react";
import { ArrowUpRight, Clock3, ListChecks } from "lucide-react";
import { LeaveIntelligencePreview } from "../../../../interfaces/leave/leaveManagement";
import "./LeaveImpactSummary.css";

export function parseLeaveIntelligence(value?: string | null): LeaveIntelligencePreview | null {
  if (!value) return null;
  try { return JSON.parse(value) as LeaveIntelligencePreview; } catch { return null; }
}

const isVisibleWarning = (code: string) => code !== "FUTURE_TASK_REVIEW" && !code.includes("SUPERVISION");

function visibleImpact(review: LeaveIntelligencePreview) {
  return JSON.stringify({
    dueBeforeLeave: review.overdueCount,
    dueDuringLeave: review.dueDuringLeaveCount,
    warnings: review.warnings.filter(w => isVisibleWarning(w.code))
      .map(w => ({ code: w.code, message: w.message })),
  });
}

export function selectLeaveImpactReview(snapshotJson?: string | null, currentJson?: string | null) {
  const saved = parseLeaveIntelligence(snapshotJson);
  const current = parseLeaveIntelligence(currentJson);
  return {
    review: current ?? saved,
    visibleChange: Boolean(saved && current && visibleImpact(saved) !== visibleImpact(current)),
  };
}

function taskListUrl(review: LeaveIntelligencePreview): string | null {
  if (!review.employeeId || !review.asOfLocalDate || !review.localStartDate || !review.localEndDate) return null;
  const params = new URLSearchParams({
    page: "1", assignee: review.employeeId, leaveImpactEmployeeId: review.employeeId,
    leaveImpactStart: review.localStartDate.slice(0, 10),
    leaveImpactEnd: review.localEndDate.slice(0, 10),
    leaveImpactAsOf: review.asOfLocalDate.slice(0, 10),
  });
  return `/tasks?${params.toString()}`;
}

export const LeaveImpactSummary: React.FC<{ review?: LeaveIntelligencePreview | null }> = ({ review }) => {
  if (!review) return <p className="m-0 text-xs text-amber-700 dark:text-amber-300">Task review unavailable. Please refresh before proceeding.</p>;
  const dueOrOverdue = review.overdueCount + review.dueDuringLeaveCount;
  const link = taskListUrl(review);
  const shortNotice = review.warnings.some(w => w.code === "SHORT_NOTICE_VACATION");
  const otherWarnings = review.warnings.filter(w => w.code !== "SHORT_NOTICE_VACATION" && isVisibleWarning(w.code));
  return <section className="space-y-2" aria-label="Leave impact review">
    {shortNotice && <div className="leave-impact-notice flex gap-2.5 rounded-xl p-3" role="status">
      <Clock3 size={19} className="leave-impact-notice-icon mt-0.5 shrink-0" aria-hidden="true" />
      <div><p className="leave-impact-notice-title m-0 text-sm font-semibold leading-5">Less than two weeks’ notice</p>
        <p className="leave-impact-notice-copy m-0 mt-1 text-[13px] leading-5">This vacation begins in fewer than 14 calendar days. This notice does not block approval.</p></div>
    </div>}
    {dueOrOverdue > 0 ? <a href={link || undefined} aria-disabled={!link} className={`leave-impact-task-card group flex items-center gap-2.5 rounded-xl p-3 no-underline ${link ? "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-500" : "cursor-default"}`}>
      <span className="leave-impact-task-icon rounded-lg p-2"><ListChecks size={18} aria-hidden="true" /></span>
      <span className="min-w-0 flex-1"><span className="leave-impact-task-title block text-sm font-semibold leading-5">{dueOrOverdue} open task{dueOrOverdue === 1 ? " is" : "s are"} due before or during this leave</span>
        <span className="leave-impact-task-meta mt-1 block text-[13px] leading-5">{review.overdueCount} due before leave · {review.dueDuringLeaveCount} due during leave{link ? " · View tasks" : ""}</span></span>
      {link && <ArrowUpRight size={19} className="leave-impact-task-arrow shrink-0" aria-hidden="true" />}
    </a> : <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-950 dark:bg-emerald-400/10 dark:text-emerald-100">No open directly assigned tasks are due before or during this leave.</div>}
    {otherWarnings.map(w => <p key={w.code} className="m-0 text-xs leading-relaxed text-slate-600 dark:text-zinc-300">{w.message}</p>)}
  </section>;
};
